import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';

import {
  AppointmentsHttpService,
  mapPlanningToAppointmentStatus,
  PlanningDetailsResponse,
  PrescriptionPlanningStatus,
} from '@lib/prescriptions/data-access';
import {
  AppointmentDetailsFormComponent,
  AppointmentNotesFormComponent,
} from '@lib/prescriptions/ui';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { map, take } from 'rxjs';

import { MatIcon } from '@angular/material/icon';

import { ActionCommentRequestBody, ActionResponseBody, ActionType, EntityType } from '@lib/core/models/action.interface';
import { fetchMemberProfile } from '@lib/members/data-access';
import { selectMemberProfile } from '@lib/members/data-access';

import { calculateAge } from '@lib/commons/util-functions/calculate-age.function';
import { findActiveContract } from '@lib/commons/util-functions/handle-contract.function';

import { MemberContract } from '@lib/core/models/contract.interface';
import { MemberProfile } from '@lib/core/models/member.interface';

import { ContractItemComponent } from '@lib/shared-ui/commons/contract-item/contract-item.component';
import { SpinLoadingComponent } from '@lib/shared-ui/commons/spin-loading/spin-loading.component';

import { AppointmentGuaranteeComponent } from './appointment-guarantee/appointment-guarantee.component';

@Component({
  selector: 'members-appointment',
  imports: [
    DatePipe,
    MatIcon,
    RouterLink,
    ContractItemComponent,
    TranslatePipe,
    SpinLoadingComponent,
    AppointmentDetailsFormComponent,
    AppointmentNotesFormComponent,
    AppointmentGuaranteeComponent,
  ],
  templateUrl: './member-appointment.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MemberAppointmentComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject(Store);
  private readonly appointmentsHttpService = inject(AppointmentsHttpService);
  private readonly destroyRef = inject(DestroyRef);

  private prescriptionId: string;
  private planningId: string;
  protected readonly memberId = signal<string>(null);

  protected readonly detailsFormRef = viewChild(AppointmentDetailsFormComponent);
  protected readonly notesFormRef = viewChild(AppointmentNotesFormComponent);

  protected readonly memberProfile = this.store.selectSignal<MemberProfile>(selectMemberProfile);

  protected readonly loadingNote = signal<boolean>(true);
  protected readonly noteSaved = signal<boolean>(false);
  protected readonly appointmentNote = signal<ActionResponseBody | null>(null);
  protected readonly planningDetails = signal<PlanningDetailsResponse | null>(null);

  protected readonly detailsSaved = signal<boolean>(true);
  protected readonly savingDetails = signal<boolean>(false);

  protected readonly age = computed<number>(() => (this.memberProfile()?.birthDate ? calculateAge(this.memberProfile().birthDate) : null));
  protected readonly lastContract = computed<MemberContract | null>(() => {
    return this.memberProfile() ? findActiveContract(this.memberProfile()?.contracts) : null;
  });

  ngOnInit() {
    // The router reuses this component instance when navigating between appointments,
    // so react to param changes instead of reading a one-time snapshot.
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.prescriptionId = params.get('prescriptionId');
      this.planningId = params.get('planningId');
      this.memberId.set(params.get('id'));

      this.initProfile();
      this.loadDetails();
    });
  }

  private initProfile() {
    this.store.dispatch(fetchMemberProfile({ memberId: this.memberId() }));
  }

  private loadDetails() {
    this.resetAppointmentState();

    this.appointmentsHttpService
      .getPlanningDetails(this.prescriptionId, this.planningId)
      .pipe(take(1))
      .subscribe({
        next: details => {
          this.planningDetails.set(details);
          if (details.appointmentId) {
            this.getNote(details.appointmentId);
          } else {
            this.loadingNote.set(false);
          }
        },
      });
  }

  private resetAppointmentState() {
    this.planningDetails.set(null);
    this.appointmentNote.set(null);
    this.loadingNote.set(true);
    this.noteSaved.set(false);
    this.detailsSaved.set(true);
  }

  protected saveDetails() {
    const formRef = this.detailsFormRef();
    if (!formRef) return;

    const newStatus = formRef.currentStatus;
    const originalStatus = this.planningDetails().status;

    if (newStatus === originalStatus) return;

    this.savingDetails.set(true);

    if (newStatus === PrescriptionPlanningStatus.Cancelled) {
      this.appointmentsHttpService
        .cancelAppointment(this.prescriptionId, this.planningDetails().planningId)
        .pipe(take(1))
        .subscribe({
          next: () => this.onSaveSuccess(newStatus),
          error: () => this.savingDetails.set(false),
        });
    } else {
      const appointmentStatus = mapPlanningToAppointmentStatus(newStatus);

      this.appointmentsHttpService
        .updateAppointmentStatus(this.planningDetails().appointmentId, appointmentStatus)
        .pipe(take(1))
        .subscribe({
          next: () => this.onSaveSuccess(newStatus),
          error: () => this.savingDetails.set(false),
        });
    }
  }

  private onSaveSuccess(newStatus: PrescriptionPlanningStatus) {
    this.planningDetails.update(d => ({ ...d, status: newStatus }));
    this.detailsSaved.set(true);
    this.savingDetails.set(false);
  }

  protected cancelChanges() {
    this.detailsFormRef()?.reset();
    this.detailsSaved.set(true);
  }

  protected saveNote() {
    const notesRef = this.notesFormRef();
    if (!notesRef?.isValid) return;

    this.loadingNote.set(true);
    if (this.appointmentNote()) this.updateNote();
    else this.addNote();
  }

  private addNote() {
    const appointmentId = this.planningDetails()?.appointmentId;
    if (!appointmentId) {
      this.loadingNote.set(false);
      return;
    }

    const commentRequestBody: ActionCommentRequestBody = {
      type: ActionType.AppointmentNote,
      entityType: EntityType.Appointment,
      entityId: appointmentId,
      content: this.notesFormRef().currentValue,
    };

    this.appointmentsHttpService
      .createAppointmentNote(commentRequestBody)
      .pipe(take(1))
      .subscribe(value => {
        this.appointmentNote.set(value);
        this.loadingNote.set(false);
        this.noteSaved.set(true);
      });
  }

  private updateNote() {
    this.appointmentsHttpService
      .updateAppointmentNote(this.appointmentNote().id, this.notesFormRef().currentValue)
      .pipe(take(1))
      .subscribe(value => {
        this.appointmentNote.set(value);
        this.loadingNote.set(false);
        this.noteSaved.set(true);
      });
  }

  protected rollbackNoteInForm() {
    this.notesFormRef()?.reset();
  }

  private getNote(appointmentId: string) {
    return this.appointmentsHttpService
      .searchAppointmentNote(appointmentId)
      .pipe(
        map(response => response.value[0]),
        take(1)
      )
      .subscribe(note => {
        if (note && note?.status !== 'Undefined') {
          this.appointmentNote.set(note);
        }
        this.loadingNote.set(false);
      });
  }
}
